/**
 * Shared page sizing for list/grid screens on wide windows (web desktop,
 * tablets, landscape). Pure so it can be unit-tested, and shared so Maps,
 * Bots, Hall of Fame and the home screen keep the same gutters and caps.
 *
 * Tiers follow the home screen: phone < 720 <= tablet < 1024 <= desktop.
 * Content is centred: the horizontal inset is the tier's side padding or,
 * once the window passes the cap, whatever centres a `maxWidth` column.
 */

export type PageTier = 'phone' | 'tablet' | 'desktop'

export const PAGE_BREAKPOINTS = { tablet: 720, desktop: 1024 } as const
export const PAGE_SIDE_PADDING = { phone: 12, tablet: 16, desktop: 48 } as const
/** Widest the main content runs on any screen (matches the home screen). */
export const PAGE_MAX_WIDTH = 1120

export interface PageLayoutOptions {
  /** Content cap; defaults to PAGE_MAX_WIDTH. */
  maxWidth?: number
  /** Grid columns per tier; defaults to a single column everywhere. */
  columns?: Partial<Record<PageTier, number>>
  /** Space between grid items, both directions. */
  gap?: number
}

export interface PageLayout {
  tier: PageTier
  /** Left/right padding for the scroll content (side padding, or more to centre the cap). */
  inset: number
  /** Width of the content box between the insets. */
  contentWidth: number
  columns: number
  /** Width of one grid item so `columns` items plus gaps fill `contentWidth` exactly. */
  itemWidth: number
  gap: number
}

export function pageTier(width: number): PageTier {
  return width >= PAGE_BREAKPOINTS.desktop ? 'desktop' : width >= PAGE_BREAKPOINTS.tablet ? 'tablet' : 'phone'
}

export function pageLayout(width: number, options: PageLayoutOptions = {}): PageLayout {
  const { maxWidth = PAGE_MAX_WIDTH, gap = 12 } = options
  const safeWidth = Number.isFinite(width) && width > 0 ? width : 0
  const tier = pageTier(safeWidth)
  const padding = PAGE_SIDE_PADDING[tier]
  const inset = Math.max(padding, (safeWidth - maxWidth) / 2)
  const contentWidth = Math.max(0, safeWidth - inset * 2)
  const columns = Math.max(1, Math.floor(options.columns?.[tier] ?? 1))
  const itemWidth = columns === 1 ? contentWidth : Math.floor((contentWidth - gap * (columns - 1)) / columns)
  return { tier, inset, contentWidth, columns, itemWidth, gap }
}
